class cartSidebar extends HTMLElement {
    constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);
        this.cartButton = document.getElementById("cart-icon-bubble");
        this.cartButton?.addEventListener("click", (event) => {
            event.preventDefault();
            this.toggle(event.target);
        });

        this.bindCartUpdate();
        this.querySelector(".modal__close-button").addEventListener(
            "click",
            this.close.bind(this)
        );
        this.addEventListener("keydown", this.handleKeydown.bind(this));
    }

    handleBodyClick(event) {
        if (
            !this.cartButton.contains(event.target) &&
            !this.contains(event.target)
        ) {
            // unset active element when clicked somewhere on body
            // to prevent page jump back to active element once focus restored
            this.setActiveElement(null);
            this.close();
        }
    }

    handleKeydown(event) {
        if (event.code.toUpperCase() === "ESCAPE") this.close();
    }

    open(openedBy) {
        const focusElement =
            this.querySelector('[name="checkout"]') ||
            this.querySelector(".modal__close-button");

        if (this.dataset.status === "open") return;

        this.dataset.status = "open";
        this.reinitPreloadImages(); // reinit preload images
        document.body.dataset.cartStatus = this.dataset.status;
        if (this.parentNode.tagName === "BODY") {
            document.body.style.width = `${window.visualViewport.width}px`;
            document.body.style.overflow = "hidden";
        }

        document.body.addEventListener("click", this.onBodyClick);

        // add short delay so browser could find element to focus
        // when there is animations it can be tricky for browser
        setTimeout(() => {
            if (openedBy) this.setActiveElement(openedBy);
            trapFocus(this, focusElement);
        }, 100);

        // clean up old messages before output
        this.querySelectorAll(".message-wrapper").forEach((el) => el.remove());
    }

    close() {
        document.body.dataset.cartStatus = this.dataset.status = "close";
        document.body.removeEventListener("click", this.onBodyClick);
        removeTrapFocus(this.activeElement);
        if (!this.closest("header"))
            setTimeout(() => {
                document.body.style.width = "";
                document.body.style.overflow = "";
            }, 200);
    }

    toggle(toggledBy) {
        if (this.dataset.status === "open") this.close();
        else this.open(toggledBy);
    }

    reinitPreloadImages() {
        const cartItems = document.getElementById("cart-sidebar-items");
        const list =
            cartItems && cartItems.querySelectorAll(".media--blur-image");
        if (!list.length) return;

        _preloadImg?.(list, true);
    }

    renderContents(parsedState) {
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
            document.getElementById(section.id).innerHTML =
                this.getSectionInnerHTML(
                    parsedState.sections?.[section.id],
                    section.selector
                );
        });

        this.classList.remove("updating");
        this.revealStickyHeaderAndOpen() || this.open();
    }

    revealStickyHeaderAndOpen(openedBy) {
        const sticky = this.closest("sticky-header");
        const header = sticky && sticky.header;

        // sticky header already revealed
        if (header && header.classList.contains("section-header-sticky"))
            return false;

        if (sticky && sticky.reveal) {
            let revealDuration =
                header && getComputedStyle(header).transitionDuration;

            if (revealDuration.endsWith("s")) {
                // css value in seconds
                sticky.reveal();
                setTimeout(
                    this.open.bind(this, openedBy),
                    parseFloat(revealDuration) * 1000
                );

                return true;
            }
        }

        return false;
    }

    getSectionsToRender() {
        return [
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
            },
            {
                id: "cart-sidebar-items",
                section: "cart-sidebar-items",
            },
            {
                id: "cart-sidebar-footer",
                section: "cart-sidebar-footer",
            },
        ];
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector).innerHTML;
    }

    updateSections(source = {}) {
        this.getSectionsToRender().forEach(({ id, section, selector }) => {
            const tagretSection = document.getElementById(id);
            const targetElement =
                tagretSection.querySelector(selector) || tagretSection;

            targetElement.innerHTML = this.getSectionInnerHTML(
                source[section],
                selector
            );
        });
    }

    reload() {
        this.classList.add("updating");

        return fetch(
            window.location.pathname +
                "?sections=" +
                this.getSectionsToRender()
                    .map((section) => section.id)
                    .join(",")
        )
            .then((response) => response.text())
            .then((text) => JSON.parse(text))
            .then((json) => {
                this.updateSections(json);
                this.classList.remove("updating");

                return json;
            });
    }

    bindCartUpdate() {
        const debouncedOnChange = debounce((event) => {
            const input = event.target;

            this.classList.add("updating");
            this.updateQuantity(input.dataset.line, input.value);
        }, 300);

        // Handle remove link clicks (immediate, no debounce)
        this.addEventListener("click", (event) => {
            const link = event.target.closest("a.cart-item__remove");
            if (!link) return;

            event.preventDefault();
            this.classList.add("updating");
            
            // Extract line from URL
            const url = new URL(link.href);
            const line = url.searchParams.get("line");
            this.updateQuantity(line, 0);
        });

        // Listen to input changes with debounce
        // The quantity-input custom element handles +/- button clicks
        // and dispatches 'change' events, which we debounce here
        this.addEventListener("change", debouncedOnChange);
        this.addEventListener("input", debouncedOnChange);
    }

    async updateQuantity(line, quantity) {
        // Get the item being updated
        const cartItem = document.querySelector(`.cart-sidebar__item[data-line="${line}"]`);
        if (!cartItem) {
            console.error('Cart item not found for line:', line);
            return;
        }
        
        const bundleId = cartItem.dataset.bundleId;
        const parentBundleId = cartItem.dataset.parentBundleId;
        
        // Check if this is a nested product (has bundle_id) - if so, prevent quantity changes
        // Nested products should only have their quantity changed via their parent
        if (bundleId) {
            // This is a nested product - its quantity is controlled by its parent
            // We could show an error message here if needed
            this.classList.remove("updating");
            return;
        }
        
        // Collect all lines that need updating (parent + children)
        const linesToUpdate = [line];
        
        // If this parent has a bundle ID, find all nested products that belong to it
        if (parentBundleId) {
            const nestedItems = document.querySelectorAll(`.cart-sidebar__item[data-bundle-id="${parentBundleId}"]`);
            nestedItems.forEach(item => {
                linesToUpdate.push(item.dataset.line);
            });
        }

        // Update each line sequentially
        try {
            for (const lineToUpdate of linesToUpdate) {
                const body = JSON.stringify({
                    line: lineToUpdate,
                    quantity: parseInt(quantity),
                    sections: this.getSectionsToRender().map((section) => section.id),
                });

                const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
                const data = await response.json();
                
                if (data.errors) {
                    let errorMessage = data.errors;
                    if (errorMessage == "Cart Error")
                        errorMessage = window.cartStrings.quantityError;
                    this.renderQtyError(line, errorMessage);
                    return;
                }
            }
            
            // After all updates, refresh the cart sections
            const finalBody = JSON.stringify({
                sections: this.getSectionsToRender().map((section) => section.id),
            });
            
            const finalResponse = await fetch(
                window.location.pathname + "?sections=" + this.getSectionsToRender().map((section) => section.id).join(",")
            );
            const sections = await finalResponse.json();
            this.updateSections(sections);
            
        } catch (error) {
            console.error('Error updating quantities:', error);
            this.renderQtyError(line, window.cartStrings.quantityError);
        } finally {
            this.classList.remove("updating");
            this.reinitPreloadImages(); // reinit preload images
        }
    }

    renderQtyError(item, error) {
        const cartItems = document.getElementById("cart-sidebar-items");
        const cartItem = cartItems.querySelector(
            `.cart-sidebar__item[data-line="${item}"]`
        );
        let message = this.querySelector(".message-wrapper");
        if (!message) {
            message = createFromTemplate("message-wrapper");
        }

        cartItems.insertBefore(message, cartItem);
        message.querySelector(".error-message").innerHTML = error;
        message.removeAttribute("hidden");
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
}

customElements.define("cart-sidebar", cartSidebar);
